import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { UserProfileService } from '../services/user-profile.service';
import { MsalHelperService } from '../services/msal-helper.service';

type Status = 'idle' | 'sending' | 'sent' | 'verified' | 'error';

@Component({
    selector: 'app-contact-email-section',
    templateUrl: './contact-email-section.component.html',
    styleUrls: ['./contact-email-section.component.scss'],
    standalone: false,
})
export class ContactEmailSectionComponent implements OnInit, OnDestroy {
    contactEmail$ = this.userProfile.contactEmail$;
    contactEmailVerified$ = this.userProfile.contactEmailVerified$;
    contactEmailPending$ = this.userProfile.contactEmailPending$;

    email = '';
    status: Status = 'idle';
    message: string | null = null;
    cooldownUntil = 0;
    // When already verified, the replace form stays hidden behind a "Change"
    // button until the user opts in.
    showReplace = false;

    private subscription = new Subscription();

    constructor(
        private userProfile: UserProfileService,
        private msalHelperService: MsalHelperService,
    ) {}

    ngOnInit(): void {
        this.userProfile.ensureLoaded();

        // Pre-fill the empty-state input with the user's AAD sign-in email so
        // first-time setup is one click. Only seeds when the field is still
        // blank — won't clobber a typed value or override the cleared state of
        // the "Replace email" form (which sets email='' on its own).
        this.subscription.add(
            this.msalHelperService.user$.subscribe((user) => {
                if (!this.email && user?.username) {
                    this.email = user.username;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    get cooldownSec(): number {
        const remaining = Math.ceil((this.cooldownUntil - Date.now()) / 1000);
        return remaining > 0 ? remaining : 0;
    }

    async onSend(): Promise<void> {
        if (this.status === 'sending') return;
        const trimmed = (this.email || '').trim();
        if (!trimmed) {
            this.status = 'error';
            this.message = 'Enter an email address.';
            return;
        }
        this.status = 'sending';
        this.message = null;
        try {
            const result = await this.userProfile.requestActivation(trimmed);
            if (!result.ok) {
                if (result.retryAfterSec) {
                    this.cooldownUntil = Date.now() + result.retryAfterSec * 1000;
                    this.status = 'error';
                    this.message = `Please wait ${result.retryAfterSec}s before requesting another link.`;
                } else if (result.error === 'Invalid email address') {
                    this.status = 'error';
                    this.message = 'That doesn’t look like a valid email address.';
                } else {
                    this.status = 'error';
                    this.message = 'We couldn’t send the activation email. Try again in a moment.';
                }
                return;
            }
            this.cooldownUntil = Date.now() + 30_000;
            this.status = 'sent';
            this.message = `Activation link sent to ${trimmed}. Click it to finish.`;
        } catch {
            this.status = 'error';
            this.message = 'Something went wrong sending the email.';
        }
    }

    async onResend(): Promise<void> {
        const pending = this.contactEmailPending$.getValue();
        if (!pending) return;
        this.email = pending;
        await this.onSend();
    }

    onClearPending(): void {
        this.userProfile.contactEmailPending$.next(null);
        this.email = '';
        this.status = 'idle';
        this.message = null;
    }

    async onRefresh(): Promise<void> {
        await this.userProfile.refresh();
    }

    onChangeAfterVerified(): void {
        // Reveal the replace form. Submitting a new email lets the backend purge
        // any previous pending row and rewrite pending.
        this.showReplace = true;
        this.email = '';
        this.status = 'idle';
        this.message = null;
    }

    onCancelReplace(): void {
        this.showReplace = false;
        this.email = '';
        this.status = 'idle';
        this.message = null;
    }
}
